// src/components/WorldChoropleth.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { scaleSequentialLog } from 'd3-scale';
import { interpolateYlOrRd } from 'd3-scale-chromatic';
import topo from 'world-atlas/countries-110m.json';
import { feature as topojsonFeature } from 'topojson-client';

// Einmalig in GeoJSON umwandeln (kein fetch nötig)
const worldGeoJSON = topojsonFeature(topo, topo.objects.countries);

// ------------- Helpers: Name-Normalisierung & Aliasse ----------------
const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/\s*\(.*?\)\s*/g, '')     // Klammerzusätze entfernen
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// Mapping zwischen unterschiedlichen Namensvarianten
const NAME_ALIASES = new Map([
  ['united states', 'united states of america'],
  ['russia', 'russian federation'],
  ['south korea', 'korea republic of'],
  ['north korea', 'korea democratic people’s republic of'],
  ['czech republic', 'czechia'],
  ['cape verde', 'cabo verde'],
  ['ivory coast', 'cote d’ivoire'],
  ['eswatini', 'swaziland'],
  ['syrian arab republic', 'syria'],
  ['lao', 'laos'],
  ['burma', 'myanmar'],
  ['macedonia', 'north macedonia'],
  ['viet nam', 'vietnam'],
]);

export default function WorldChoropleth({ data, height = 360, tooltip = true }) {
  // data: [{ code?: 'DE', name?: 'Germany', views: 123 }, …]
  const [hover, setHover] = useState(null);

  // responsive width
  const ref = useRef(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    const onResize = () => ref.current && setWidth(ref.current.clientWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // code -> views
  const viewsByCode = useMemo(() => {
    const m = new Map();
    (data || []).forEach(d => {
      const c = String(d.code || '').toUpperCase();
      if (c) m.set(c, d.views || 0);
    });
    return m;
  }, [data]);

  // name(normalized) -> views (inkl. Alias)
  const viewsByName = useMemo(() => {
    const m = new Map();
    (data || []).forEach(d => {
      const n = normalize(d.name);
      if (n) {
        m.set(n, d.views || 0);
        const alias = NAME_ALIASES.get(n);
        if (alias) m.set(normalize(alias), d.views || 0);
      }
    });
    return m;
  }, [data]);

  const max = useMemo(() => Math.max(1, ...(data || []).map(d => d.views || 0)), [data]);
  const color = useMemo(() => scaleSequentialLog([1, max], interpolateYlOrRd), [max]);

  const projection = useMemo(
    () => geoNaturalEarth1().fitSize([width, height], worldGeoJSON),
    [width, height]
  );
  const path = useMemo(() => geoPath(projection), [projection]);

  const iso2FromProps = p =>
    (p?.ISO_A2 || p?.iso_a2 || p?.A2 || '').toString().toUpperCase();

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={width} height={height} role="img" aria-label="Weltkarte: Pageviews nach Land">
        <g>
          {worldGeoJSON.features.map((f, i) => {
            const iso2 = iso2FromProps(f.properties);
            const natName = f.properties?.name || '';
            const nKey = normalize(natName);

            // 1) erst Code matchen, 2) dann Name (inkl. Alias)
            let v = 0;
            if (iso2 && viewsByCode.has(iso2)) {
              v = viewsByCode.get(iso2) || 0;
            } else if (viewsByName.has(nKey)) {
              v = viewsByName.get(nKey) || 0;
            }

            const fill = v ? color(v) : '#EEE';
            return (
              <path
                key={i}
                d={path(f)}
                fill={fill}
                stroke="#bbb"
                strokeWidth={0.5}
                onMouseEnter={() => tooltip && setHover({ name: natName || iso2 || 'Unknown', iso2, v })}
                onMouseLeave={() => tooltip && setHover(null)}
              />
            );
          })}
        </g>
      </svg>

      {/* Legende */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: 12, opacity: .7 }}>Weniger</span>
        <div
          style={{
            height: 8, flex: 1,
            background: `linear-gradient(to right, ${Array.from({length:10}, (_,i)=>color(Math.max(1, Math.pow(max, i/9)))).join(',')})`
          }}
        />
        <span style={{ fontSize: 12, opacity: .7 }}>Mehr</span>
      </div>

      {tooltip && hover && (
        <div style={{
          marginTop: 6, fontSize: 13, padding: '6px 8px',
          background: '#fff', border: '1px solid #ddd', borderRadius: 6,
          display: 'inline-block', boxShadow: '0 1px 4px rgba(0,0,0,.08)'
        }}>
          <strong>{hover.name}</strong>{hover.iso2 ? ` (${hover.iso2})` : ''}: {hover.v}
        </div>
      )}
    </div>
  );
}
